import { Node, mergeAttributes } from "@tiptap/core";
import { Plugin, PluginKey } from "@tiptap/pm/state";

export interface FootnoteOptions {
  HTMLAttributes: Record<string, unknown>;
}

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    footnote: {
      /**
       * Insert a footnote
       */
      insertFootnote: (content: string) => ReturnType;
      /**
       * Update footnote content
       */
      updateFootnote: (pos: number, content: string) => ReturnType;
    };
  }
}

/**
 * TipTap extension for inline footnotes
 * Renders as superscript numbers with hover preview
 */
export const Footnote = Node.create<FootnoteOptions>({
  name: "footnote",
  group: "inline",
  inline: true,
  atom: true,

  addOptions() {
    return {
      HTMLAttributes: {},
    };
  },

  addAttributes() {
    return {
      content: {
        default: "",
        parseHTML: (element) => element.getAttribute("data-footnote-content") || "",
        renderHTML: (attributes) => ({
          "data-footnote-content": attributes.content,
        }),
      },
      number: {
        default: 1,
        parseHTML: (element) => parseInt(element.getAttribute("data-footnote-number") || "1", 10),
        renderHTML: (attributes) => ({
          "data-footnote-number": attributes.number,
        }),
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'span[data-type="footnote"]',
      },
    ];
  },

  renderHTML({ node, HTMLAttributes }) {
    const content = node.attrs.content as string;
    const number = node.attrs.number as number;

    return [
      "span",
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, {
        "data-type": "footnote",
        "data-footnote-content": content,
        "data-footnote-number": number,
        class: "footnote-marker",
        title: content,
      }),
      ["sup", {}, String(number)],
    ];
  },

  addCommands() {
    return {
      insertFootnote:
        (content: string) =>
        ({ commands, state }) => {
          // Count existing footnotes to determine number
          let footnoteCount = 0;
          state.doc.descendants((node) => {
            if (node.type.name === "footnote") {
              footnoteCount++;
            }
          });

          return commands.insertContent({
            type: this.name,
            attrs: { content, number: footnoteCount + 1 },
          });
        },

      updateFootnote:
        (pos: number, content: string) =>
        ({ tr, dispatch }) => {
          if (dispatch) {
            tr.setNodeMarkup(pos, undefined, { content });
            dispatch(tr);
          }
          return true;
        },
    };
  },

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: new PluginKey("footnoteNumbering"),
        appendTransaction: (transactions, _oldState, newState) => {
          // Renumber footnotes when document changes
          let needsRenumber = false;
          for (const tr of transactions) {
            if (tr.docChanged) {
              needsRenumber = true;
              break;
            }
          }

          if (!needsRenumber) return null;

          const { tr } = newState;
          let footnoteIndex = 0;
          let hasChanges = false;

          newState.doc.descendants((node, pos) => {
            if (node.type.name === "footnote") {
              footnoteIndex++;
              if (node.attrs.number !== footnoteIndex) {
                tr.setNodeMarkup(pos, undefined, {
                  ...node.attrs,
                  number: footnoteIndex,
                });
                hasChanges = true;
              }
            }
          });

          return hasChanges ? tr : null;
        },
      }),
    ];
  },

  addKeyboardShortcuts() {
    return {
      // Ctrl+Alt+F to insert footnote
      "Mod-Alt-f": () => {
        const content = prompt("Enter footnote content:");
        if (content) {
          return this.editor.commands.insertFootnote(content);
        }
        return false;
      },
    };
  },
});

export default Footnote;
